'use client';
import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/Header';
import Footer from '@/components/Footer';
import { useForm } from 'react-hook-form';
import ScrollTopButton from '@/components/ScrollTopButton';

// 1. 최적화된 Leaflet 지도 컴포넌트
function LeafletCompanyMap({ officeLatLng, officeName }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null); // 지도 객체 중복 생성을 막기 위한 ref
  const { lat, lng } = officeLatLng;

  useEffect(() => {
    // Leaflet CSS 동적 로딩
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Leaflet JS 동적 로딩
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.async = true;

    script.onload = () => {
      // 지도 초기화 (중복 방지 체크)
      if (mapRef.current && window.L && !mapInstance.current) {
        // 지도 인스턴스 생성
        const map = window.L.map(mapRef.current).setView([lat, lng], 15);
        mapInstance.current = map;

        // 무료 오픈소스 타일 레이어 추가 (API 키 불필요)
        window.L.tileLayer(
          'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          {
            attribution: '© OpenStreetMap contributors',
            maxZoom: 19,
          },
        ).addTo(map);

        // 마커 및 팝업 추가
        window.L.marker([lat, lng])
          .addTo(map)
          .bindPopup(`<b>${officeName}</b>`)
          .openPopup();

        // 렌더링 시점에 지도가 깨져 보이는 현상 방지
        setTimeout(() => {
          map.invalidateSize();
        }, 200);
      }
    };

    document.head.appendChild(script);

    // 클린업 함수: 컴포넌트가 사라질 때 지도 객체 제거 (메모리 누수 및 에러 방지)
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [lat, lng, officeName]);

  return (
    <div
      ref={mapRef}
      className="relative z-0 bg-gray-100"
      style={{
        width: '100%',
        height: '400px',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    />
  );
}

// 2. 메인 페이지 컴포넌트
export default function ContactPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [mailModalStatus, setMailModalStatus] = useState(false);

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm();

  // 회사 위치 좌표
  const headOfficeLatLng = { lat: 36.366626709573936, lng: 127.40737766460774 };
  const pangyoOfficeLatLng = { lat: 37.4122238741141, lng: 127.09863167853143 };

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const onSubmit = (data) => {
    fetch('/api/inquire', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then(() => {
        reset();
        setMailModalStatus(true);
        alert('문의가 성공적으로 접수되었습니다.');
      })
      .catch((error) => console.error('Error:', error));
  };

  return (
    <div className="font-sans min-h-screen">
      <Header />
      <ScrollTopButton />

      {/* 상단 섹션 */}
      <section className="pt-32 lg:pt-40 lg:pb-24">
        <div className="px-4 sm:px-6 lg:px-44">
          <div
            className={`flex items-center gap-4 mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
          >
            <div className="w-20 h-1 bg-blue-600"></div>
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
              Get In Touch
            </span>
          </div>
          <h2
            className={`text-5xl md:text-7xl font-black text-gray-800 mb-6 transition-all duration-700 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
          >
            Contact Us
          </h2>
          <p
            className={`text-base md:text-xl text-gray-600 max-w-3xl leading-relaxed transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
          >
            언제든지 문의해 주시면 신속하게 답변드리겠습니다.
          </p>
        </div>
      </section>

      <main className="flex flex-col text-gray-800">
        {/* 지도 섹션 */}
        <section className="px-4 pb-20 mx-auto pt-14 bg-gray-50 w-full">
          {/* 본사 지도 */}
          <div className="flex flex-col justify-start pt-10 lg:px-36">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-2/3">
                <LeafletCompanyMap
                  officeLatLng={headOfficeLatLng}
                  officeName="대전 본사"
                />
              </div>
              <div className="lg:w-1/3">
                <h3 className="pb-3 mb-8 text-2xl font-bold border-b-2 border-gray-800">
                  본사
                </h3>
                <p className="font-semibold text-lg">
                  대전광역시 대덕구 대화로 120, 가온비즈타워 610호
                </p>
                <div className="mt-4 space-y-1 text-gray-600">
                  <p>TEL | 042-629-3201</p>
                  <p>FAX | 042-629-3200</p>
                </div>
              </div>
            </div>
          </div>

          {/* 연구소 지도 */}
          <div className="flex flex-col justify-start pt-28 lg:px-36">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-2/3">
                <LeafletCompanyMap
                  officeLatLng={pangyoOfficeLatLng}
                  officeName="판교 연구소"
                />
              </div>
              <div className="lg:w-1/3">
                <h3 className="pb-3 mb-8 text-2xl font-bold border-b-2 border-gray-800">
                  연구소
                </h3>
                <p className="font-semibold text-lg">
                  경기도 성남시 수정구 창업로 17, 판교아이스퀘어포레나 302호
                </p>
                <div className="mt-4 space-y-1 text-gray-600">
                  <p>TEL | 031-607-3203</p>
                  <p>FAX | 031-607-3200</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 문의 폼 섹션 (기존과 동일하되 디자인 소폭 정돈) */}
        <section className="px-4 sm:px-6 lg:px-44 py-20 bg-white">
          <form onSubmit={handleSubmit(onSubmit)} className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-12 gap-12">
              <div className="lg:col-span-4">
                <h2 className="text-4xl font-black text-gray-800 mb-4">
                  문의하기
                </h2>
                <p className="text-gray-600">
                  사업 제안이나 궁금하신 점을 남겨주세요.
                </p>
              </div>
              <div className="lg:col-span-8 space-y-6">
                {/* 각 입력 필드 */}
                {[
                  { id: 'company', label: '회사명', type: 'text' },
                  { id: 'name', label: '성함(직책)', type: 'text' },
                  { id: 'phoneNumber', label: '연락처', type: 'tel' },
                  { id: 'email', label: '이메일', type: 'email' },
                ].map((field) => (
                  <div
                    key={field.id}
                    className="border-t border-gray-200 py-4 flex flex-col sm:flex-row sm:items-center"
                  >
                    <label className="sm:w-32 font-bold text-gray-700">
                      {field.label}
                    </label>
                    <div className="flex-1 mt-2 sm:mt-0">
                      <input
                        {...register(field.id, {
                          required: `${field.label}을 입력해주세요`,
                        })}
                        type={field.type}
                        className="w-full p-3 border border-gray-300 focus:border-blue-600 outline-none transition-all"
                        placeholder={`${field.label}을 입력해주세요`}
                      />
                      {errors[field.id] && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors[field.id].message}
                        </p>
                      )}
                    </div>
                  </div>
                ))}

                <div className="border-t border-gray-200 py-4 flex flex-col sm:flex-row">
                  <label className="sm:w-32 font-bold text-gray-700 pt-2">
                    문의내용
                  </label>
                  <div className="flex-1 mt-2 sm:mt-0">
                    <textarea
                      {...register('contents', {
                        required: '내용을 입력해주세요',
                        minLength: {
                          value: 10,
                          message: '10자 이상 작성해주세요',
                        },
                      })}
                      rows={5}
                      className="w-full p-3 border border-gray-300 focus:border-blue-600 outline-none resize-none"
                      placeholder="상세 내용을 입력해주세요"
                    />
                    {errors.contents && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.contents.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end pt-6">
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-16 transition-all shadow-lg"
                  >
                    문의 보내기
                  </button>
                </div>
              </div>
            </div>
          </form>
        </section>
      </main>
      <Footer />
    </div>
  );
}
